import { useCallback, useEffect, useState } from 'react';

/**
 * Small inline button shown next to a location field when the user has typed lat/lng
 * coordinates instead of an address. Clicking it triggers a backend reverse-geocode and
 * populates the form's address fields.
 *
 * This implements the "Look up address" UX from the geocoding migration plan section 3.2.4:
 * coordinates are respected (no auto-overwrite) but a one-click address lookup is offered.
 *
 * The button is intentionally minimal — forms render it adjacent to their location field;
 * the form supplies the lookup handler that knows how to update its own fields.
 */

const LAT_LNG_PATTERN = /^\s*\(?\s*-?\d{1,3}(?:\.\d+)?\s*,\s*-?\d{1,3}(?:\.\d+)?\s*\)?\s*$/;

export interface LookupAddressButtonProps {
    /** The current value of the associated location field. */
    locationValue: string;
    /**
     * Invoked when the user clicks "Look up address". The handler should call the geocode
     * endpoint with the current coordinates and update the form's address fields with the
     * resolved formatted address + components.
     */
    onLookupRequested: () => void;
    /**
     * Invoked when the user clicks "Use coordinates only". The handler should clear the
     * form's address-component fields (leaving the coordinates intact).
     */
    onRevertToCoords: () => void;
    /**
     * Whether the form currently has populated address fields from a previous lookup.
     * Controls which label is shown.
     */
    addressPopulated?: boolean;
    style?: React.CSSProperties;
}

export function LookupAddressButton({
    locationValue,
    onLookupRequested,
    onRevertToCoords,
    addressPopulated = false,
    style,
}: LookupAddressButtonProps) {
    const [show, setShow] = useState(false);

    useEffect(() => {
        setShow(typeof locationValue === 'string' && LAT_LNG_PATTERN.test(locationValue));
    }, [locationValue]);

    const handleClick = useCallback(() => {
        if (addressPopulated) {
            onRevertToCoords();
        } else {
            onLookupRequested();
        }
    }, [addressPopulated, onLookupRequested, onRevertToCoords]);

    if (!show) return null;

    return (
        <button
            type="button"
            className="btn btn-link btn-xs"
            style={{ padding: '2px 6px', fontSize: '0.85em', ...style }}
            onClick={handleClick}
        >
            {addressPopulated ? 'Use coordinates only' : 'Look up address'}
        </button>
    );
}

(window as any).LookupAddressButtonReact = LookupAddressButton;
